import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { labAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useLabDemo } from '../context/LabDemoContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, Send, Sparkles, FlaskConical, Brain, FileText, Workflow,
  ChevronRight, Lightbulb, MoreVertical, FileDown, Copy, FolderPlus,
  RefreshCw, CheckCircle, AlertTriangle, Target, Compass, Palette,
  Megaphone, Settings, CheckSquare, DollarSign, Users, Heart, BarChart,
  Layers, ListChecks, FileCheck, Calculator, TrendingUp, Calendar, Map, Zap,
  Play, Check, ArrowRight, Eye, ChevronDown, ExternalLink, RotateCcw,
  FolderKanban, Clock, Rocket, ArrowUpRight, X
} from 'lucide-react';
import { DESIGN } from '../components/DesignSystem';
import { useNavigate } from 'react-router-dom';

// ================================================
// DEMO CONVERSATION DATA
// ================================================
const DEMO_THREAD = {
  id: 'demo-q3-growth',
  title: 'Q3 Growth Strategy Session',
  mode: 'structured',
  lane: 'strategy',
  level: 'L4',
  message_count: 10,
  artifact_count: 1,
  isDemo: true,
  created_at: new Date().toISOString()
};

const DEMO_MESSAGES = [
  {
    id: 'dm-1',
    role: 'user',
    content: `I need help thinking through our Q3 growth strategy. We're a creative agency and we're planning to expand into two new regional markets — Pacific Northwest and Southwest. This is a significant move and I want to make sure we approach it correctly.

What framework would you recommend for thinking through this expansion?`,
    created_at: '2026-02-19T09:00:00Z'
  },
  {
    id: 'dm-2',
    role: 'assistant',
    content: `This is exactly the kind of strategic challenge that benefits from structured cognitive analysis. Let me break this down across the relevant cognitive domains:

**Vision & Expansion (Lane 1)**
Market opportunity assessment — evaluating the addressable market, competitive landscape, and growth potential in both regions. The Pacific Northwest has strong tech and sustainability sectors while the Southwest is experiencing rapid business growth.

**Strategy & Architecture (Lane 2)**
The expansion blueprint — defining market entry approach, resource allocation, phasing strategy, and success metrics. Key question: simultaneous vs sequential market entry.

**Marketing (Lane 4)**
Demand generation in new markets — brand awareness campaigns, local partnerships, case study development, and regional positioning strategy.

**Operations (Lane 5)**
Logistics and team deployment — hiring local talent, establishing physical presence, operational infrastructure, and regional management structure.

Would you like me to dive deeper into any of these domains? I'm particularly curious about your current team capacity for this expansion.`,
    lane: 'strategy',
    level: 'L4',
    created_at: '2026-02-19T09:02:00Z'
  },
  {
    id: 'dm-3',
    role: 'user',
    content: `Yes, team capacity is a major concern. We have a strong leadership team but I'm worried about stretching too thin. What are your thoughts on our ability to execute this?`,
    created_at: '2026-02-19T09:05:00Z'
  },
  {
    id: 'dm-4',
    role: 'assistant',
    content: `I've analyzed your team structure and I need to surface a significant concern:

**⚠️ Cognitive Load Analysis**

Your current team has **L4 coverage gaps in the Operations lane** for the new regions. Looking at your org structure:

- Your Operations Manager (David) is already at 85% capacity managing current operations
- You have no designated L3+ Operations leaders for either new region
- Launching two markets simultaneously creates **compound execution risk**

**Risk Factors:**
1. **Decision Bottleneck** — All strategic decisions would funnel through your CEO and COO
2. **Culture Dilution** — Rapid expansion without cultural anchors in new regions
3. **Quality Control** — Creative agencies live on reputation; thin coverage risks delivery quality

**My Assessment:** Simultaneous dual-market entry would push your organization past healthy cognitive thresholds. The probability of execution issues rises significantly.

What's your timeline flexibility? I have a recommendation that could de-risk this significantly.`,
    lane: 'strategy',
    level: 'L4',
    created_at: '2026-02-19T09:08:00Z'
  },
  {
    id: 'dm-5',
    role: 'user',
    content: `That's a really helpful analysis. We do have some flexibility — the board wants us in both markets by end of year, but the exact timing is up to us. What would you recommend?`,
    created_at: '2026-02-19T09:12:00Z'
  },
  {
    id: 'dm-6',
    role: 'assistant',
    content: `Here's my structured recommendation for a **Phased Regional Expansion**:

---

## Recommended Approach

### Phase 1: Pre-Launch Preparation (Weeks 1-6)
**Focus: Building foundation before market entry**

1. **Hire/designate an Operations L3 Lead for Region 1 (Pacific Northwest)**
   - This person becomes your operational anchor
   - Can be internal promotion or external hire
   - Must be in place before boots on the ground

2. **Run 6-week pre-launch marketing campaign in Pacific Northwest**
   - Build brand awareness before you arrive
   - Generate initial leads and conversations
   - Create regional case studies/partnerships

3. **Prepare regional infrastructure**
   - Office/co-working space
   - Local vendor relationships
   - Regional tech stack setup

---

### Phase 2: Market Entry - Pacific Northwest (Weeks 7-14)
**Focus: Disciplined market entry with full support**

- Launch with dedicated leadership attention
- Operations L3 Lead manages daily execution
- Weekly L4 decision checkpoints with HQ
- Aggressive first 90-day client acquisition

---

### Phase 3: Market Entry - Southwest (Weeks 15-24)
**Focus: Repeat playbook with learnings applied**

- Hire Operations L3 Lead for Region 2
- Apply lessons from Pacific Northwest launch
- Begin parallel 6-week pre-launch campaign
- Full market entry once Region 1 is stabilized

---

## Why This Works

✅ **De-risks execution** — One region gets full attention at a time
✅ **Builds organizational muscle** — Learn from Region 1 before Region 2
✅ **Maintains quality** — Your reputation stays intact
✅ **Creates clear milestones** — Board can see structured progress

You'd still hit both markets by end of year, but with a much higher probability of success.

Would you like me to turn this into an executable plan with specific tasks and assignments?`,
    lane: 'strategy',
    level: 'L4',
    created_at: '2026-02-19T09:18:00Z'
  },
  {
    id: 'dm-7',
    role: 'user',
    content: `This is exactly what I needed. Yes, please turn this into an executable plan. I want tasks I can assign, milestones I can track, and focus blocks I can put in my calendar.`,
    created_at: '2026-02-19T09:22:00Z'
  },
  {
    id: 'dm-8',
    role: 'assistant',
    content: `Perfect. Here's the complete execution structure:

---

## Q3 Regional Expansion Strategy
### Executable Plan Summary

---

**PROJECT: Q3 Regional Expansion Strategy**
Primary Lane: Strategy & Architecture
Level: L4 — DECIDE + LEAD

---

### 3 Phases

**Phase 1: Pre-Launch Preparation** (Weeks 1-6)
**Phase 2: Pacific Northwest Market Entry** (Weeks 7-14)
**Phase 3: Southwest Market Entry** (Weeks 15-24)

---

### 8 Core Tasks

| Task | Lane | Level | Phase |
|------|------|-------|-------|
| Conduct regional market analysis for both markets | Strategy | L3 | 1 |
| Identify and brief Operations L3 Lead for Pacific Northwest | Operations | L4 | 1 |
| Develop 6-week pre-launch marketing campaign for Pacific Northwest | Marketing | L3 | 1 |
| Create regional team deployment plan | Operations | L3 | 1 |
| Set up regional infrastructure and logistics (Pacific Northwest) | Operations | L2 | 2 |
| Launch Pacific Northwest market entry | Revenue | L4 | 2 |
| Evaluate Pacific Northwest performance before Southwest launch | Strategy | L4 | 3 |
| Begin Southwest pre-launch preparation | Marketing | L3 | 3 |

---

### 4 Executive Focus Blocks

1. **Weekly Expansion Strategy Review** — 60 min, recurring
2. **L4 Decision Checkpoint** — Before each phase transition
3. **Regional Director Briefing Sessions** — Bi-weekly with new leads
4. **Board Update on Expansion Progress** — Monthly

---

### 2 Team Role Requirements

1. **Operations L3 Lead — Pacific Northwest** (Hire/designate by Week 4)
2. **Operations L3 Lead — Southwest** (Hire/designate by Week 12)

---

## Ready for Execution

This strategic plan is ready to be converted into live work inside CognitiveOS. 

**Use the Convert button above** to transform this conversation into:
- 📁 A fully structured Project with 3 phases
- ✅ 8 actionable Tasks with lane assignments
- 🎯 4 Focus blocks for your Command Center
- 👥 2 Team role recommendations for Structure

Click **Convert** to bring this strategy to life.`,
    lane: 'strategy',
    level: 'L4',
    created_at: '2026-02-19T09:30:00Z'
  }
];

// ================================================
// DEMO CONVERSION DATA
// ================================================
const DEMO_DETECTED_ITEMS = {
  project: {
    count: 1,
    data: {
      name: 'Q3 Regional Expansion Strategy',
      description: 'Phased regional expansion into Pacific Northwest and Southwest markets',
      lane: 'strategy',
      level: 'L4',
      phases: [
        { name: 'Pre-Launch Preparation', weeks: '1-6', status: 'Planned' },
        { name: 'Pacific Northwest Market Entry', weeks: '7-14', status: 'Planned' },
        { name: 'Southwest Market Entry', weeks: '15-24', status: 'Planned' }
      ]
    }
  },
  tasks: {
    count: 8,
    data: [
      { name: 'Conduct regional market analysis for both markets', lane: 'strategy', level: 'L3', assigneeRole: 'Strategy Lead', phase: 1 },
      { name: 'Identify and brief Operations L3 Lead for Pacific Northwest', lane: 'operations', level: 'L4', assigneeRole: 'COO', phase: 1 },
      { name: 'Develop 6-week pre-launch marketing campaign for Pacific Northwest', lane: 'marketing', level: 'L3', assigneeRole: 'Marketing Lead', phase: 1 },
      { name: 'Create regional team deployment plan', lane: 'operations', level: 'L3', assigneeRole: 'Operations Manager', phase: 1 },
      { name: 'Set up regional infrastructure and logistics (Pacific Northwest)', lane: 'operations', level: 'L2', assigneeRole: 'Operations L3 Lead', phase: 2 },
      { name: 'Launch Pacific Northwest market entry', lane: 'revenue', level: 'L4', assigneeRole: 'CEO', phase: 2 },
      { name: 'Evaluate Pacific Northwest performance before Southwest launch', lane: 'strategy', level: 'L4', assigneeRole: 'CEO', phase: 3 },
      { name: 'Begin Southwest pre-launch preparation', lane: 'marketing', level: 'L3', assigneeRole: 'Marketing Lead', phase: 3 }
    ]
  },
  focusBlocks: {
    count: 4,
    data: [
      { name: 'Weekly Expansion Strategy Review', duration: '60 min', type: 'Recurring', priority: 'High' },
      { name: 'L4 Decision Checkpoint', duration: '45 min', type: 'Phase Gate', priority: 'Critical' },
      { name: 'Regional Director Briefing Sessions', duration: '30 min', type: 'Bi-weekly', priority: 'Medium' },
      { name: 'Board Update on Expansion Progress', duration: '60 min', type: 'Monthly', priority: 'High' }
    ]
  },
  teamRoles: {
    count: 2,
    data: [
      { title: 'Operations L3 Lead — Pacific Northwest', level: 'L3', lane: 'operations', deadline: 'Week 4', status: 'Open Position' },
      { title: 'Operations L3 Lead — Southwest', level: 'L3', lane: 'operations', deadline: 'Week 12', status: 'Open Position' }
    ]
  }
};

const CREATION_STEPS = [
  { id: 1, text: 'Analyzing conversation and extracting structure', duration: 800 },
  { id: 2, text: 'Creating project: Q3 Regional Expansion Strategy', duration: 1000 },
  { id: 3, text: 'Generating 8 tasks with lane assignments', duration: 1200 },
  { id: 4, text: 'Building 3 project phases with milestones', duration: 900 },
  { id: 5, text: 'Adding 4 focus blocks to Command Center', duration: 800 },
  { id: 6, text: 'Creating 2 team role recommendations in Structure', duration: 700 },
  { id: 7, text: 'Applying cognitive lane assignments', duration: 600 },
  { id: 8, text: 'Setting level requirements', duration: 500 },
  { id: 9, text: 'Linking all items to source conversation', duration: 700 },
  { id: 10, text: 'Finalizing and saving to your workspace', duration: 900 }
];

// Icon mapping
const ICONS = {
  Compass, Target, Palette, Megaphone, Settings, CheckSquare, DollarSign, 
  Users, Heart, BarChart, Map, FolderKanban: Layers, FileCheck, ListChecks,
  Calculator, TrendingUp, Calendar, AlertTriangle, Brain, FileText, Workflow
};

// Mode configurations with enhanced styling
const MODES = {
  free_think: { name: 'Free Think', icon: Brain, color: '#A855F7', description: 'Open exploration', glow: 'rgba(168, 85, 247, 0.4)' },
  structured: { name: 'Structured', icon: FileText, color: '#3B82F6', description: 'Guided artifacts', glow: 'rgba(59, 130, 246, 0.4)' },
  operator: { name: 'Operator', icon: Workflow, color: '#10B981', description: 'Workflow templates', glow: 'rgba(16, 185, 129, 0.4)' }
};

// Lane colors for preview
const LANE_COLORS = {
  vision: '#38BDF8',
  strategy: '#1E3A5F',
  creative: '#A855F7',
  marketing: '#FB923C',
  operations: '#22C55E',
  execution: '#14B8A6',
  revenue: '#EAB308',
  relationships: '#F472B6',
  culture: '#C4B5FD',
  finance: '#64748B'
};

const LANE_NAMES = {
  vision: 'Vision & Expansion',
  strategy: 'Strategy & Architecture',
  creative: 'Creative',
  marketing: 'Marketing',
  operations: 'Operations',
  execution: 'Execution',
  revenue: 'Revenue',
  relationships: 'Relationships',
  culture: 'Culture',
  finance: 'Finance & Analysis'
};

// ================================================
// CONVERT WIZARD COMPONENT
// ================================================
const ConvertWizard = ({ isOpen, onClose, onComplete, threadTitle, isDemo }) => {
  const [step, setStep] = useState('select'); // select, preview, creating, complete
  const [selectedTypes, setSelectedTypes] = useState(['all']);
  const [projectName, setProjectName] = useState('Q3 Regional Expansion Strategy');
  const [selectedLane, setSelectedLane] = useState('strategy');
  const [selectedLevel, setSelectedLevel] = useState('L4');
  const [creationProgress, setCreationProgress] = useState(0);
  const [currentCreationStep, setCurrentCreationStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [itemsCreated, setItemsCreated] = useState(0);
  const navigate = useNavigate();

  const totalItems = DEMO_DETECTED_ITEMS.project.count + 
    DEMO_DETECTED_ITEMS.tasks.count + 
    DEMO_DETECTED_ITEMS.focusBlocks.count + 
    DEMO_DETECTED_ITEMS.teamRoles.count;

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStep('select');
      setSelectedTypes(['all']);
      setProjectName('Q3 Regional Expansion Strategy');
      setSelectedLane('strategy');
      setSelectedLevel('L4');
      setCreationProgress(0);
      setCurrentCreationStep(0);
      setCompletedSteps([]);
      setItemsCreated(0);
    }
  }, [isOpen]);

  // Run creation animation
  const runCreation = async () => {
    setStep('creating');
    setCompletedSteps([]);
    setItemsCreated(0);
    
    for (let i = 0; i < CREATION_STEPS.length; i++) {
      setCurrentCreationStep(i);
      await new Promise(resolve => setTimeout(resolve, CREATION_STEPS[i].duration));
      setCompletedSteps(prev => [...prev, CREATION_STEPS[i].id]);
      setCreationProgress(((i + 1) / CREATION_STEPS.length) * 100);
      setItemsCreated(Math.floor(((i + 1) / CREATION_STEPS.length) * totalItems));
    }
    
    // Small delay before showing complete
    await new Promise(resolve => setTimeout(resolve, 500));
    setStep('complete');
    onComplete?.();
  };

  const handleViewSection = (section) => {
    onClose();
    navigate(`/${section}`);
  };

  const conversionTypes = [
    { id: 'project', name: 'Project', icon: FolderKanban, count: DEMO_DETECTED_ITEMS.project.count, color: DESIGN.colors.primary },
    { id: 'tasks', name: 'Tasks Only', icon: CheckSquare, count: DEMO_DETECTED_ITEMS.tasks.count, color: '#3B82F6' },
    { id: 'focus', name: 'Focus Blocks', icon: Target, count: DEMO_DETECTED_ITEMS.focusBlocks.count, color: '#8B5CF6' },
    { id: 'roles', name: 'Team Roles', icon: Users, count: DEMO_DETECTED_ITEMS.teamRoles.count, color: '#F472B6' },
    { id: 'all', name: 'All Mixed', icon: Layers, count: totalItems, color: DESIGN.colors.primary }
  ];

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`${step === 'creating' || step === 'complete' ? 'max-w-3xl' : 'max-w-2xl'} p-0 overflow-hidden`}
        style={{ 
          backgroundColor: step === 'creating' || step === 'complete' ? '#0F172A' : DESIGN.colors.labCard, 
          borderColor: DESIGN.colors.labBorder 
        }}
      >
        {/* Step: Select */}
        {step === 'select' && (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle style={{ color: DESIGN.colors.labText }}>Convert to Execution</DialogTitle>
              <DialogDescription style={{ color: DESIGN.colors.labTextMuted }}>
                Transform this conversation into actionable items
              </DialogDescription>
            </DialogHeader>
            
            <div className="p-6">
              {/* AI Detection Summary */}
              <div 
                className="p-4 rounded-xl mb-6 border"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary}10 0%, ${DESIGN.colors.primary}05 100%)`,
                  borderColor: `${DESIGN.colors.primary}30`
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${DESIGN.colors.primary}20` }}
                  >
                    <Sparkles className="w-5 h-5" style={{ color: DESIGN.colors.primary }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: DESIGN.colors.labText }}>
                      AI Analysis Complete
                    </p>
                    <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>
                      CognitiveOS has detected <strong>3 phases</strong>, <strong>8 tasks</strong>, <strong>2 team roles</strong>, and <strong>4 focus blocks</strong> in this conversation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Conversion Type Buttons */}
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: DESIGN.colors.labTextMuted }}>
                Select what to create
              </p>
              <div className="grid grid-cols-5 gap-3 mb-6">
                {conversionTypes.map(type => {
                  const Icon = type.icon;
                  const isSelected = selectedTypes.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedTypes([type.id])}
                      className="relative p-4 rounded-xl text-center transition-all"
                      style={{
                        backgroundColor: isSelected ? `${type.color}20` : DESIGN.colors.labBackground,
                        border: `2px solid ${isSelected ? type.color : 'transparent'}`,
                        boxShadow: isSelected ? `0 0 15px ${type.color}30` : 'none'
                      }}
                    >
                      {/* Count Badge */}
                      <div 
                        className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ backgroundColor: type.color, color: 'white' }}
                      >
                        {type.count}
                      </div>
                      <Icon 
                        className="w-6 h-6 mx-auto mb-2" 
                        style={{ color: isSelected ? type.color : DESIGN.colors.labTextMuted }} 
                      />
                      <p 
                        className="text-xs font-semibold"
                        style={{ color: isSelected ? type.color : DESIGN.colors.labTextMuted }}
                      >
                        {type.name}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Project Name & Lane/Level */}
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold mb-2 block" style={{ color: DESIGN.colors.labTextMuted }}>
                    Project Name
                  </label>
                  <Input
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="border-0"
                    style={{ 
                      backgroundColor: DESIGN.colors.labBackground,
                      color: DESIGN.colors.labText
                    }}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold mb-2 block" style={{ color: DESIGN.colors.labTextMuted }}>
                      Primary Lane (Auto-detected)
                    </label>
                    <Select value={selectedLane} onValueChange={setSelectedLane}>
                      <SelectTrigger 
                        className="border-0"
                        style={{ backgroundColor: DESIGN.colors.labBackground, color: DESIGN.colors.labText }}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}>
                        {Object.entries(LANE_NAMES).map(([id, name]) => (
                          <SelectItem key={id} value={id} style={{ color: DESIGN.colors.labText }}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: LANE_COLORS[id] }} />
                              {name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold mb-2 block" style={{ color: DESIGN.colors.labTextMuted }}>
                      Level (Auto-detected)
                    </label>
                    <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                      <SelectTrigger 
                        className="border-0"
                        style={{ backgroundColor: DESIGN.colors.labBackground, color: DESIGN.colors.labText }}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}>
                        {['L1', 'L2', 'L3', 'L4'].map(level => (
                          <SelectItem key={level} value={level} style={{ color: DESIGN.colors.labText }}>
                            {level} - {DESIGN.levels[level]?.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div 
              className="p-6 border-t flex justify-end gap-3"
              style={{ borderColor: DESIGN.colors.labBorder }}
            >
              <Button 
                variant="outline" 
                onClick={onClose}
                style={{ borderColor: DESIGN.colors.labBorder, color: DESIGN.colors.labText }}
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setStep('preview')}
                className="flex items-center gap-2"
                style={{ 
                  backgroundColor: DESIGN.colors.labBackground,
                  color: DESIGN.colors.labText
                }}
              >
                <Eye className="w-4 h-4" />
                Preview
              </Button>
              <Button 
                onClick={runCreation}
                className="flex items-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white'
                }}
              >
                <Rocket className="w-4 h-4" />
                Create Now
              </Button>
            </div>
          </>
        )}

        {/* Step: Preview */}
        {step === 'preview' && (
          <>
            <DialogHeader className="p-6 pb-0">
              <DialogTitle style={{ color: DESIGN.colors.labText }}>Preview: What Will Be Created</DialogTitle>
              <DialogDescription style={{ color: DESIGN.colors.labTextMuted }}>
                Review before creating {totalItems} items
              </DialogDescription>
            </DialogHeader>
            
            <ScrollArea className="h-[500px] p-6">
              {/* Project Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <FolderKanban className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                  <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.labText }}>Project</h4>
                </div>
                <div 
                  className="p-4 rounded-xl border-l-4"
                  style={{ 
                    backgroundColor: DESIGN.colors.labBackground,
                    borderLeftColor: LANE_COLORS[selectedLane]
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-semibold" style={{ color: DESIGN.colors.labText }}>{projectName}</p>
                      <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>{DEMO_DETECTED_ITEMS.project.data.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: `${LANE_COLORS[selectedLane]}20`, color: LANE_COLORS[selectedLane] }}
                      >
                        {LANE_NAMES[selectedLane]}
                      </span>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: `${DESIGN.levels[selectedLevel]?.color}20`, color: DESIGN.levels[selectedLevel]?.color }}
                      >
                        {selectedLevel}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {DEMO_DETECTED_ITEMS.project.data.phases.map((phase, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: DESIGN.colors.labCard, color: DESIGN.colors.labTextMuted }}
                      >
                        {phase.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tasks Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckSquare className="w-4 h-4" style={{ color: '#3B82F6' }} />
                  <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.labText }}>Tasks ({DEMO_DETECTED_ITEMS.tasks.count})</h4>
                </div>
                <div className="space-y-2">
                  {DEMO_DETECTED_ITEMS.tasks.data.map((task, idx) => (
                    <div 
                      key={idx}
                      className="p-3 rounded-xl flex items-center justify-between"
                      style={{ backgroundColor: DESIGN.colors.labBackground }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: LANE_COLORS[task.lane] }} />
                        <span className="text-sm" style={{ color: DESIGN.colors.labText }}>{task.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>{task.assigneeRole}</span>
                        <span 
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: `${DESIGN.levels[task.level]?.color}20`, color: DESIGN.levels[task.level]?.color }}
                        >
                          {task.level}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Focus Blocks Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Target className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                  <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.labText }}>Focus Blocks ({DEMO_DETECTED_ITEMS.focusBlocks.count})</h4>
                </div>
                <div className="space-y-2">
                  {DEMO_DETECTED_ITEMS.focusBlocks.data.map((block, idx) => (
                    <div 
                      key={idx}
                      className="p-3 rounded-xl flex items-center justify-between"
                      style={{ backgroundColor: DESIGN.colors.labBackground }}
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                        <span className="text-sm" style={{ color: DESIGN.colors.labText }}>{block.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>{block.duration}</span>
                        <span 
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={{ 
                            backgroundColor: block.priority === 'Critical' ? DESIGN.colors.errorSubtle : DESIGN.colors.amberSubtle,
                            color: block.priority === 'Critical' ? DESIGN.colors.error : DESIGN.colors.amber
                          }}
                        >
                          {block.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Team Roles Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" style={{ color: '#F472B6' }} />
                  <h4 className="text-sm font-bold" style={{ color: DESIGN.colors.labText }}>Team Roles ({DEMO_DETECTED_ITEMS.teamRoles.count})</h4>
                </div>
                <div className="space-y-2">
                  {DEMO_DETECTED_ITEMS.teamRoles.data.map((role, idx) => (
                    <div 
                      key={idx}
                      className="p-3 rounded-xl flex items-center justify-between"
                      style={{ backgroundColor: DESIGN.colors.labBackground }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: LANE_COLORS[role.lane] }} />
                        <div>
                          <span className="text-sm" style={{ color: DESIGN.colors.labText }}>{role.title}</span>
                          <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>Deadline: {role.deadline}</p>
                        </div>
                      </div>
                      <span 
                        className="text-xs px-2 py-1 rounded"
                        style={{ backgroundColor: '#8B5CF620', color: '#8B5CF6' }}
                      >
                        LAB Recommended
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Summary */}
              <div 
                className="p-4 rounded-xl text-center"
                style={{ backgroundColor: `${DESIGN.colors.primary}10`, border: `1px solid ${DESIGN.colors.primary}30` }}
              >
                <p className="text-lg font-bold" style={{ color: DESIGN.colors.primary }}>
                  {totalItems} items will be created
                </p>
                <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>
                  Across Projects, Tasks, Focus, and Structure
                </p>
              </div>
            </ScrollArea>

            <div 
              className="p-6 border-t flex justify-between"
              style={{ borderColor: DESIGN.colors.labBorder }}
            >
              <Button 
                variant="outline" 
                onClick={() => setStep('select')}
                style={{ borderColor: DESIGN.colors.labBorder, color: DESIGN.colors.labText }}
              >
                Back to Edit
              </Button>
              <Button 
                onClick={runCreation}
                className="flex items-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: `0 0 20px ${DESIGN.colors.primary}40`
                }}
              >
                <CheckCircle className="w-4 h-4" />
                Confirm & Create
              </Button>
            </div>
          </>
        )}

        {/* Step: Creating - Premium Progress Screen */}
        {step === 'creating' && (
          <div className="p-8 text-center">
            {/* Logo with Pulse */}
            <div className="relative inline-block mb-8">
              <FlaskConical className="w-16 h-16" style={{ color: DESIGN.colors.primary }} />
              <div 
                className="absolute inset-0 animate-ping"
                style={{ opacity: 0.3 }}
              >
                <FlaskConical className="w-16 h-16" style={{ color: DESIGN.colors.primary }} />
              </div>
              <div 
                className="absolute inset-0 rounded-full"
                style={{ 
                  background: `radial-gradient(circle, ${DESIGN.colors.primary}30 0%, transparent 70%)`,
                  filter: 'blur(20px)'
                }}
              />
            </div>

            {/* Counter */}
            <div className="mb-6">
              <p className="text-5xl font-black mb-2" style={{ color: DESIGN.colors.primary }}>
                {itemsCreated}/{totalItems}
              </p>
              <p className="text-sm" style={{ color: DESIGN.colors.labTextMuted }}>Items created</p>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-2 rounded-full overflow-hidden mb-8" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${creationProgress}%`,
                  background: `linear-gradient(90deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryLight} 100%)`,
                  boxShadow: `0 0 20px ${DESIGN.colors.primary}50`
                }}
              />
            </div>

            {/* Steps List */}
            <div className="text-left space-y-3 max-w-md mx-auto">
              {CREATION_STEPS.map((creationStep, idx) => {
                const isCompleted = completedSteps.includes(creationStep.id);
                const isCurrent = currentCreationStep === idx && !isCompleted;
                
                return (
                  <div 
                    key={creationStep.id}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      {isCompleted ? (
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center animate-scale-in"
                          style={{ 
                            backgroundColor: DESIGN.colors.primary,
                            boxShadow: `0 0 10px ${DESIGN.colors.primary}60`
                          }}
                        >
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      ) : isCurrent ? (
                        <div 
                          className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
                          style={{ borderColor: `${DESIGN.colors.primary} transparent ${DESIGN.colors.primary} ${DESIGN.colors.primary}` }}
                        />
                      ) : (
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                        />
                      )}
                    </div>
                    <span 
                      className={`text-sm transition-all ${isCompleted || isCurrent ? 'opacity-100' : 'opacity-40'}`}
                      style={{ color: isCompleted ? DESIGN.colors.primary : DESIGN.colors.labTextMuted }}
                    >
                      {creationStep.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step: Complete - Success Screen */}
        {step === 'complete' && (
          <div className="p-8">
            {/* Success Animation */}
            <div className="text-center mb-8">
              <div className="relative inline-block mb-6">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center animate-scale-in"
                  style={{ 
                    background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                    boxShadow: `0 0 40px ${DESIGN.colors.primary}50`
                  }}
                >
                  <Check className="w-12 h-12 text-white" />
                </div>
                <div 
                  className="absolute inset-0 rounded-full animate-ping-slow"
                  style={{ 
                    border: `3px solid ${DESIGN.colors.primary}`,
                    opacity: 0.4
                  }}
                />
              </div>
              <h2 className="text-2xl font-black mb-2" style={{ color: 'white' }}>
                Your strategic plan is now live in CognitiveOS
              </h2>
              <p className="text-sm" style={{ color: DESIGN.colors.labTextMuted }}>
                {totalItems} items created across Projects, Tasks, Focus, and Structure
              </p>
            </div>

            {/* Navigation Cards */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[
                { section: 'projects', icon: FolderKanban, name: 'Projects', desc: '1 project created with 3 phases and 8 tasks', color: DESIGN.colors.primary },
                { section: 'tasks', icon: CheckSquare, name: 'Tasks', desc: '8 tasks created across Strategy & Operations lanes', color: '#3B82F6' },
                { section: 'focus', icon: Target, name: 'Focus', desc: '4 focus blocks added to your Command Center', color: '#8B5CF6' },
                { section: 'structure', icon: Users, name: 'Structure', desc: '2 team role recommendations added', color: '#F472B6' }
              ].map(card => {
                const Icon = card.icon;
                return (
                  <button
                    key={card.section}
                    onClick={() => handleViewSection(card.section)}
                    className="p-5 rounded-xl text-left transition-all hover:scale-105 group"
                    style={{ 
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: `1px solid rgba(255,255,255,0.1)`
                    }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${card.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: card.color }} />
                      </div>
                      <span className="font-bold" style={{ color: 'white' }}>{card.name}</span>
                    </div>
                    <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>{card.desc}</p>
                    <div className="flex items-center gap-1 mt-3 text-xs font-semibold" style={{ color: card.color }}>
                      View <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={onClose}
                className="flex-1 flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'white'
                }}
              >
                <FlaskConical className="w-4 h-4" />
                Return to LAB
              </Button>
              <Button
                onClick={() => handleViewSection('projects')}
                className="flex-1 flex items-center justify-center gap-2"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: `0 0 20px ${DESIGN.colors.primary}40`
                }}
              >
                <ExternalLink className="w-4 h-4" />
                View All Created Items
              </Button>
            </div>
          </div>
        )}

        <style>{`
          @keyframes scale-in {
            from { transform: scale(0); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in {
            animation: scale-in 0.3s ease-out forwards;
          }
          @keyframes ping-slow {
            0% { transform: scale(1); opacity: 0.4; }
            100% { transform: scale(1.5); opacity: 0; }
          }
          .animate-ping-slow {
            animation: ping-slow 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

// ================================================
// MAIN LAB COMPONENT
// ================================================
const Lab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { demoCompleted, conversionDate, completeDemo, resetDemo: resetDemoContext } = useLabDemo();
  const [config, setConfig] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Thread settings
  const [mode, setMode] = useState('free_think');
  const [selectedLane, setSelectedLane] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedOutputType, setSelectedOutputType] = useState(null);
  
  // UI state
  const [showOperators, setShowOperators] = useState(false);
  const [showConvertWizard, setShowConvertWizard] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [activeTab, setActiveTab] = useState('chat');
  
  const messagesEndRef = useRef(null);
  
  // Check if user is in demo mode
  const isDemo = useMemo(() => {
    return user?.email === 'demo@cognitiveos.com' || user?.isDemo;
  }, [user]);

  // Fetch config and threads on mount
  useEffect(() => {
    const init = async () => {
      try {
        const [configRes, threadsRes] = await Promise.all([
          labAPI.getConfig(),
          labAPI.getThreads()
        ]);
        setConfig(configRes.data);
        
        let allThreads = threadsRes.data.threads || [];
        
        // In demo mode, add the demo thread if not already present
        if (isDemo) {
          const hasDemoThread = allThreads.some(t => t.id === DEMO_THREAD.id);
          if (!hasDemoThread) {
            allThreads = [DEMO_THREAD, ...allThreads];
          }
        }
        
        setThreads(allThreads);
        
        // Auto-select demo thread in demo mode
        if (isDemo && allThreads.length > 0) {
          const demoThread = allThreads.find(t => t.id === DEMO_THREAD.id) || allThreads[0];
          selectThread(demoThread);
        } else if (allThreads.length > 0) {
          selectThread(allThreads[0]);
        }
      } catch (error) {
        console.error('Error initializing LAB:', error);
        
        // In demo mode, still show the demo thread even if API fails
        if (isDemo) {
          setThreads([DEMO_THREAD]);
          selectThread(DEMO_THREAD);
        }
        
        toast.error('Failed to load LAB configuration');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isDemo]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectThread = async (thread) => {
    setSelectedThread(thread);
    setMode(thread.mode || 'free_think');
    setSelectedLane(thread.lane);
    setSelectedLevel(thread.level);
    setSelectedOutputType(thread.output_type);
    setRecommendation(null);
    
    // If it's the demo thread, load demo messages
    if (thread.id === DEMO_THREAD.id) {
      setMessages(DEMO_MESSAGES);
      setArtifacts([]);
      return;
    }
    
    try {
      const [messagesRes, artifactsRes] = await Promise.all([
        labAPI.getMessages(thread.id),
        labAPI.getThreadArtifacts(thread.id)
      ]);
      setMessages(messagesRes.data.messages || []);
      setArtifacts(artifactsRes.data.artifacts || []);
    } catch (error) {
      console.error('Error fetching thread data:', error);
    }
  };

  const handleNewThread = async () => {
    const title = prompt('Enter thread title:');
    if (!title) return;
    
    try {
      const response = await labAPI.createThreadV2({ 
        title, 
        mode: mode,
        lane: selectedLane,
        level: selectedLevel
      });
      const newThread = response.data.thread;
      setThreads(prev => [newThread, ...prev]);
      selectThread(newThread);
      toast.success('Thread created');
    } catch (error) {
      console.error('Error creating thread:', error);
      toast.error('Failed to create thread');
    }
  };

  const handleSend = async () => {
    if (!input.trim() || !selectedThread || sending) return;
    
    // Don't allow sending in demo thread (it's pre-loaded)
    if (selectedThread.id === DEMO_THREAD.id) {
      toast.info('This is a demo conversation. Click "Convert" to see the conversion flow!');
      return;
    }
    
    setSending(true);
    const userMessage = { 
      role: 'user', 
      content: input, 
      lane: selectedLane,
      level: selectedLevel,
      created_at: new Date().toISOString() 
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');

    try {
      const response = await labAPI.sendMessageV2(selectedThread.id, {
        content: input,
        mode: mode,
        lane: selectedLane,
        level: selectedLevel,
        output_type: selectedOutputType
      });
      
      setMessages(prev => [...prev, response.data.ai_response]);
      
      if (response.data.recommendation) {
        setRecommendation(response.data.recommendation);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, there was an error processing your request.',
        created_at: new Date().toISOString()
      }]);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateThread = async (updates) => {
    if (!selectedThread) return;
    
    // Don't update demo thread
    if (selectedThread.id === DEMO_THREAD.id) return;
    
    try {
      await labAPI.updateThread(selectedThread.id, updates);
      setSelectedThread(prev => ({ ...prev, ...updates }));
      setThreads(prev => prev.map(t => 
        t.id === selectedThread.id ? { ...t, ...updates } : t
      ));
    } catch (error) {
      console.error('Error updating thread:', error);
    }
  };

  const applyRecommendation = () => {
    if (!recommendation) return;
    
    setSelectedLane(recommendation.recommended_lane);
    setSelectedLevel(recommendation.recommended_level);
    if (recommendation.suggested_output_type) {
      setSelectedOutputType(recommendation.suggested_output_type);
    }
    
    handleUpdateThread({
      lane: recommendation.recommended_lane,
      level: recommendation.recommended_level,
      output_type: recommendation.suggested_output_type
    });
    
    setRecommendation(null);
    toast.success('Applied AI recommendation');
  };

  const handleResetDemo = () => {
    resetDemoContext();
    setMessages(DEMO_MESSAGES);
    toast.success('Demo reset! You can experience the conversion flow again.');
  };

  const handleConversionComplete = () => {
    completeDemo();
  };

  const getLaneIcon = (laneId) => {
    const iconMap = {
      vision: Compass, strategy: Target, creative: Palette,
      marketing: Megaphone, operations: Settings, execution: CheckSquare,
      revenue: DollarSign, relationships: Users, culture: Heart, finance: BarChart
    };
    return iconMap[laneId] || Target;
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-[calc(100vh-140px)]"
        style={{ backgroundColor: DESIGN.colors.labBackground }}
      >
        <div className="text-center">
          <div className="relative">
            <FlaskConical className="w-16 h-16 mx-auto mb-4" style={{ color: DESIGN.colors.primary }} />
            <div 
              className="absolute inset-0 animate-ping"
              style={{ opacity: 0.3 }}
            >
              <FlaskConical className="w-16 h-16 mx-auto" style={{ color: DESIGN.colors.primary }} />
            </div>
          </div>
          <p style={{ color: DESIGN.colors.labTextMuted }}>Initializing THE LAB...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="h-[calc(100vh-140px)] flex gap-4 -m-8 p-4"
      style={{ backgroundColor: DESIGN.colors.labBackground }}
      data-testid="lab-page"
    >
      {/* Dark Threads Sidebar */}
      <div 
        className="w-72 rounded-2xl flex flex-col overflow-hidden"
        style={{ 
          backgroundColor: DESIGN.colors.labCard,
          border: `1px solid ${DESIGN.colors.labBorder}`
        }}
      >
        {/* New Thread Button */}
        <div className="p-4">
          <button 
            onClick={handleNewThread}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all hover:shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
              color: 'white',
              boxShadow: `0 0 20px ${DESIGN.colors.primary}40`
            }}
            data-testid="new-thread-btn"
          >
            <Plus className="w-5 h-5" />
            New Thread
            <Sparkles className="w-4 h-4 opacity-70" />
          </button>
        </div>

        {/* Thread List */}
        <ScrollArea className="flex-1 px-3">
          {threads.length === 0 ? (
            <div className="p-4 text-center">
              <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: DESIGN.colors.labTextMuted }} />
              <p className="text-sm" style={{ color: DESIGN.colors.labTextMuted }}>No threads yet</p>
              <p className="text-xs mt-1" style={{ color: DESIGN.colors.labTextMuted }}>Start exploring ideas</p>
            </div>
          ) : (
            threads.map((thread) => {
              const ModeIcon = MODES[thread.mode]?.icon || Brain;
              const modeColor = MODES[thread.mode]?.color || DESIGN.colors.primary;
              const isSelected = selectedThread?.id === thread.id;
              const isDemoThread = thread.id === DEMO_THREAD.id;
              
              return (
                <button 
                  key={thread.id} 
                  onClick={() => selectThread(thread)} 
                  className="w-full text-left p-4 rounded-xl mb-2 transition-all relative"
                  style={{ 
                    backgroundColor: isSelected ? `${DESIGN.colors.primary}15` : 'transparent',
                    borderLeft: isSelected ? `3px solid ${DESIGN.colors.primary}` : '3px solid transparent',
                    boxShadow: isSelected ? `0 0 15px ${DESIGN.colors.primary}20` : 'none'
                  }}
                  data-testid={`thread-${thread.id}`}
                >
                  {isDemoThread && (
                    <span 
                      className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded"
                      style={{ 
                        background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, #8B5CF6 100%)`,
                        color: 'white'
                      }}
                    >
                      DEMO
                    </span>
                  )}
                  <p 
                    className="text-sm font-semibold mb-1 truncate pr-12"
                    style={{ color: isSelected ? DESIGN.colors.labText : DESIGN.colors.labTextMuted }}
                  >
                    {thread.title}
                  </p>
                  <div className="flex items-center gap-2 text-xs" style={{ color: DESIGN.colors.labTextMuted }}>
                    <ModeIcon className="w-3.5 h-3.5" style={{ color: modeColor }} />
                    <span>{thread.message_count || 0} msgs</span>
                    {thread.artifact_count > 0 && (
                      <>
                        <span>•</span>
                        <span>{thread.artifact_count} artifacts</span>
                      </>
                    )}
                  </div>
                  {thread.lane && (
                    <div className="mt-2">
                      <span 
                        className="text-[10px] px-2 py-1 rounded-md font-semibold"
                        style={{ 
                          backgroundColor: `${LANE_COLORS[thread.lane]}20`,
                          color: LANE_COLORS[thread.lane]
                        }}
                      >
                        {LANE_NAMES[thread.lane]}
                      </span>
                    </div>
                  )}
                </button>
              );
            })
          )}
        </ScrollArea>

        {/* Operators Button */}
        <div className="p-4 border-t" style={{ borderColor: DESIGN.colors.labBorder }}>
          <button 
            onClick={() => setShowOperators(true)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all"
            style={{ 
              background: `linear-gradient(135deg, ${MODES.operator.color}20 0%, ${MODES.operator.color}10 100%)`,
              color: MODES.operator.color,
              border: `1px solid ${MODES.operator.color}30`
            }}
            data-testid="operators-btn"
          >
            <Workflow className="w-5 h-5" />
            Operators
            <Zap className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Chat Area */}
      {selectedThread ? (
        <div 
          className="flex-1 rounded-2xl flex flex-col overflow-hidden"
          style={{ 
            backgroundColor: DESIGN.colors.labCard,
            border: `1px solid ${DESIGN.colors.labBorder}`
          }}
        >
          {/* Conversion Success Banner */}
          {demoCompleted && selectedThread.id === DEMO_THREAD.id && (
            <div 
              className="px-5 py-3 flex items-center justify-between"
              style={{ 
                background: `linear-gradient(90deg, ${DESIGN.colors.primary}15 0%, transparent 100%)`,
                borderBottom: `1px solid ${DESIGN.colors.labBorder}`
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${DESIGN.colors.primary}20` }}
                >
                  <CheckCircle className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: DESIGN.colors.labText }}>
                    This conversation was converted to 15 items
                  </p>
                  <p className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>
                    on {conversionDate}
                  </p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={() => navigate('/projects')}
                className="flex items-center gap-1"
                style={{ backgroundColor: DESIGN.colors.primary, color: 'white' }}
              >
                View Results <ArrowUpRight className="w-3 h-3" />
              </Button>
            </div>
          )}

          {/* Header */}
          <div 
            className="p-5 border-b flex-shrink-0"
            style={{ borderColor: DESIGN.colors.labBorder }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold" style={{ color: DESIGN.colors.labText }}>
                  {selectedThread?.title}
                </h2>
                {selectedThread.id === DEMO_THREAD.id && (
                  <Badge 
                    className="flex items-center gap-1"
                    style={{ 
                      background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, #8B5CF6 100%)`,
                      color: 'white'
                    }}
                  >
                    <Sparkles className="w-3 h-3" />
                    Demo Mode
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowConvertWizard(true)}
                  disabled={messages.length === 0}
                  className="border-0"
                  style={{ 
                    backgroundColor: `${DESIGN.colors.primary}15`,
                    color: DESIGN.colors.primary
                  }}
                  data-testid="convert-btn"
                >
                  <FolderPlus className="w-4 h-4 mr-1" />
                  Convert
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      style={{ color: DESIGN.colors.labTextMuted }}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end"
                    style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}
                  >
                    <DropdownMenuItem style={{ color: DESIGN.colors.labText }}>
                      <Copy className="w-4 h-4 mr-2" /> Copy Thread
                    </DropdownMenuItem>
                    <DropdownMenuItem style={{ color: DESIGN.colors.labText }}>
                      <FileDown className="w-4 h-4 mr-2" /> Export
                    </DropdownMenuItem>
                    {selectedThread.id === DEMO_THREAD.id && (
                      <>
                        <DropdownMenuSeparator style={{ backgroundColor: DESIGN.colors.labBorder }} />
                        <DropdownMenuItem 
                          onClick={handleResetDemo}
                          style={{ color: DESIGN.colors.amber }}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" /> Reset Demo
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Premium Mode Selector */}
            <div className="flex items-center gap-2 mb-4">
              {Object.entries(MODES).map(([modeId, modeConfig]) => {
                const ModeIcon = modeConfig.icon;
                const isActive = mode === modeId;
                return (
                  <button
                    key={modeId}
                    onClick={() => {
                      setMode(modeId);
                      handleUpdateThread({ mode: modeId });
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                    style={{ 
                      backgroundColor: isActive ? `${modeConfig.color}20` : 'transparent',
                      color: isActive ? modeConfig.color : DESIGN.colors.labTextMuted,
                      border: `2px solid ${isActive ? modeConfig.color : 'transparent'}`,
                      boxShadow: isActive ? `0 0 15px ${modeConfig.glow}` : 'none'
                    }}
                    data-testid={`mode-${modeId}`}
                  >
                    <ModeIcon className="w-4 h-4" />
                    {modeConfig.name}
                  </button>
                );
              })}
            </div>
            
            {/* Lane + Level Selectors */}
            <div className="flex items-center gap-3 flex-wrap">
              <Select 
                value={selectedLane || 'none'} 
                onValueChange={(val) => {
                  const newLane = val === 'none' ? null : val;
                  setSelectedLane(newLane);
                  handleUpdateThread({ lane: newLane });
                }}
              >
                <SelectTrigger 
                  className="w-48 h-9 text-sm border-0"
                  style={{ 
                    backgroundColor: DESIGN.colors.labBackground,
                    color: DESIGN.colors.labText
                  }}
                  data-testid="lane-selector"
                >
                  <SelectValue placeholder="Select Lane" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}>
                  <SelectItem value="none" style={{ color: DESIGN.colors.labTextMuted }}>No Lane</SelectItem>
                  {Object.entries(LANE_NAMES).map(([id, name]) => {
                    const Icon = getLaneIcon(id);
                    return (
                      <SelectItem key={id} value={id} style={{ color: DESIGN.colors.labText }}>
                        <span className="flex items-center gap-2">
                          <Icon className="w-4 h-4" style={{ color: LANE_COLORS[id] }} />
                          {name}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>

              <Select 
                value={selectedLevel || 'none'} 
                onValueChange={(val) => {
                  const newLevel = val === 'none' ? null : val;
                  setSelectedLevel(newLevel);
                  handleUpdateThread({ level: newLevel });
                }}
              >
                <SelectTrigger 
                  className="w-36 h-9 text-sm border-0"
                  style={{ 
                    backgroundColor: DESIGN.colors.labBackground,
                    color: DESIGN.colors.labText
                  }}
                  data-testid="level-selector"
                >
                  <SelectValue placeholder="Select Level" />
                </SelectTrigger>
                <SelectContent style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}>
                  <SelectItem value="none" style={{ color: DESIGN.colors.labTextMuted }}>No Level</SelectItem>
                  {['L1', 'L2', 'L3', 'L4'].map((level) => (
                    <SelectItem key={level} value={level} style={{ color: DESIGN.colors.labText }}>
                      <span className="flex items-center gap-2">
                        <span 
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: DESIGN.levels[level]?.color }}
                        />
                        {level} - {DESIGN.levels[level]?.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Active Selection Badges */}
              {selectedLane && (
                <span 
                  className="text-xs px-3 py-1.5 rounded-lg font-semibold"
                  style={{ 
                    backgroundColor: `${LANE_COLORS[selectedLane]}20`,
                    color: LANE_COLORS[selectedLane]
                  }}
                >
                  {LANE_NAMES[selectedLane]}
                </span>
              )}
              {selectedLevel && (
                <span 
                  className="text-xs px-3 py-1.5 rounded-lg font-semibold"
                  style={{ 
                    backgroundColor: `${DESIGN.levels[selectedLevel]?.color}20`,
                    color: DESIGN.levels[selectedLevel]?.color
                  }}
                >
                  {selectedLevel}: {DESIGN.levels[selectedLevel]?.label}
                </span>
              )}
            </div>

            {/* AI Recommendation Banner */}
            {recommendation && recommendation.confidence !== 'low' && (
              <div 
                className="mt-4 p-4 rounded-xl flex items-center justify-between"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15) 0%, rgba(234, 179, 8, 0.05) 100%)',
                  border: '1px solid rgba(234, 179, 8, 0.3)'
                }}
              >
                <div className="flex items-center gap-3">
                  <Lightbulb className="w-5 h-5" style={{ color: '#EAB308' }} />
                  <span className="text-sm" style={{ color: '#FCD34D' }}>
                    <strong>AI Suggests:</strong> {LANE_NAMES[recommendation.recommended_lane]} Lane, 
                    {' '}{recommendation.recommended_level}
                  </span>
                </div>
                <Button 
                  size="sm" 
                  onClick={applyRecommendation}
                  style={{ backgroundColor: '#EAB308', color: '#0F172A' }}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>

          {/* Chat Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <div className="px-5 border-b flex-shrink-0" style={{ borderColor: DESIGN.colors.labBorder }}>
              <TabsList style={{ backgroundColor: 'transparent' }}>
                <TabsTrigger 
                  value="chat" 
                  className="data-[state=active]:bg-white/10"
                  style={{ color: DESIGN.colors.labText }}
                >
                  Chat
                </TabsTrigger>
                <TabsTrigger 
                  value="artifacts" 
                  className="data-[state=active]:bg-white/10"
                  style={{ color: DESIGN.colors.labText }}
                >
                  Artifacts ({artifacts.length})
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="chat" className="flex-1 flex flex-col m-0 p-0 min-h-0 overflow-hidden">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-4 max-w-full">
                  {messages.map((message, index) => (
                    <div 
                      key={message.id || index} 
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`max-w-2xl rounded-2xl p-4`} 
                        style={{ 
                          backgroundColor: message.role === 'user' 
                            ? DESIGN.colors.primary
                            : DESIGN.colors.labBackground,
                          background: message.role === 'user' 
                            ? `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`
                            : DESIGN.colors.labBackground,
                          borderLeft: message.role === 'assistant' ? `3px solid ${DESIGN.colors.primary}` : 'none',
                          boxShadow: message.role === 'assistant' ? `0 0 20px ${DESIGN.colors.primary}20` : 'none',
                          color: message.role === 'user' ? 'white' : DESIGN.colors.labText
                        }}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="relative">
                              <Sparkles className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                              <div 
                                className="absolute inset-0 animate-ping"
                                style={{ opacity: 0.4 }}
                              >
                                <Sparkles className="w-4 h-4" style={{ color: DESIGN.colors.primary }} />
                              </div>
                            </div>
                            <span className="text-xs font-bold" style={{ color: DESIGN.colors.primary }}>
                              CognitiveOS AI
                            </span>
                            {message.lane && (
                              <span 
                                className="text-[10px] px-2 py-0.5 rounded"
                                style={{ 
                                  backgroundColor: `${LANE_COLORS[message.lane]}20`,
                                  color: LANE_COLORS[message.lane]
                                }}
                              >
                                {LANE_NAMES[message.lane]}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap prose prose-sm max-w-none prose-invert">
                          {message.content}
                        </div>
                        {message.role === 'assistant' && (
                          <div className="mt-4 flex items-center gap-2">
                            <button 
                              onClick={() => navigator.clipboard.writeText(message.content)}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/10"
                              style={{ color: DESIGN.colors.labTextMuted }}
                            >
                              <Copy className="w-3.5 h-3.5" /> Copy
                            </button>
                            <button 
                              onClick={() => toast.success('Saved as artifact')}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/10"
                              style={{ color: DESIGN.colors.labTextMuted }}
                            >
                              <CheckCircle className="w-3.5 h-3.5" /> Save Artifact
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Premium Input Area */}
              <div className="p-5 border-t flex-shrink-0" style={{ borderColor: DESIGN.colors.labBorder }}>
                {selectedThread?.id === DEMO_THREAD.id ? (
                  <div 
                    className="p-4 rounded-2xl text-center"
                    style={{ 
                      background: `linear-gradient(135deg, ${DESIGN.colors.primary}10 0%, #8B5CF610 100%)`,
                      border: `1px solid ${DESIGN.colors.primary}30`
                    }}
                  >
                    <p className="text-sm font-semibold mb-2" style={{ color: DESIGN.colors.labText }}>
                      This is a demo conversation
                    </p>
                    <p className="text-xs mb-4" style={{ color: DESIGN.colors.labTextMuted }}>
                      Click the <strong>Convert</strong> button above to experience how LAB conversations transform into executable work.
                    </p>
                    <Button 
                      onClick={() => setShowConvertWizard(true)}
                      className="flex items-center gap-2 mx-auto"
                      style={{ 
                        background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                        color: 'white',
                        boxShadow: `0 0 20px ${DESIGN.colors.primary}40`
                      }}
                    >
                      <Play className="w-4 h-4" />
                      Try Convert to Execution
                    </Button>
                  </div>
                ) : (
                  <>
                    <div 
                      className="flex gap-3 p-3 rounded-2xl transition-all focus-within:shadow-lg"
                      style={{ 
                        backgroundColor: DESIGN.colors.labBackground,
                        border: `1px solid ${DESIGN.colors.labBorder}`
                      }}
                    >
                      <Textarea 
                        value={input} 
                        onChange={(e) => setInput(e.target.value)} 
                        onKeyDown={(e) => { 
                          if (e.key === 'Enter' && !e.shiftKey) { 
                            e.preventDefault(); 
                            handleSend(); 
                          } 
                        }} 
                        placeholder={mode === 'structured' 
                          ? "Describe what you need. I'll create a structured artifact..." 
                          : "What are you thinking about? Let's explore..."
                        }
                        className="resize-none flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0" 
                        style={{ color: DESIGN.colors.labText }}
                        rows={2}
                        disabled={sending}
                        data-testid="message-input"
                      />
                      <button 
                        onClick={handleSend} 
                        disabled={sending || !input.trim()} 
                        className="self-end w-12 h-12 rounded-xl flex items-center justify-center transition-all disabled:opacity-50"
                        style={{ 
                          background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                          boxShadow: input.trim() ? `0 0 20px ${DESIGN.colors.primary}50` : 'none'
                        }}
                        data-testid="send-btn"
                      >
                        <Send className="w-5 h-5 text-white" />
                      </button>
                    </div>
                    <p className="text-xs mt-3" style={{ color: DESIGN.colors.labTextMuted }}>
                      Mode: <span style={{ color: MODES[mode]?.color }}>{MODES[mode]?.name}</span>
                      {selectedLane && (
                        <> • <span style={{ color: LANE_COLORS[selectedLane] }}>{LANE_NAMES[selectedLane]}</span></>
                      )}
                      {selectedLevel && (
                        <> • <span style={{ color: DESIGN.levels[selectedLevel]?.color }}>{selectedLevel}</span></>
                      )}
                      {sending && <> • <span style={{ color: DESIGN.colors.primary }}>Thinking...</span></>}
                    </p>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="artifacts" className="flex-1 m-0 p-5 overflow-y-auto min-h-0">
              {artifacts.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <FileText className="w-14 h-14 mx-auto mb-4 opacity-30" style={{ color: DESIGN.colors.labTextMuted }} />
                    <p className="font-medium" style={{ color: DESIGN.colors.labText }}>No artifacts yet</p>
                    <p className="text-sm mt-1" style={{ color: DESIGN.colors.labTextMuted }}>
                      Save AI outputs as artifacts to track and version them
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  {artifacts.map((artifact) => (
                    <div 
                      key={artifact.id}
                      className="p-5 rounded-xl"
                      style={{ 
                        backgroundColor: DESIGN.colors.labBackground,
                        border: `1px solid ${DESIGN.colors.labBorder}`
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold" style={{ color: DESIGN.colors.labText }}>
                            {artifact.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span 
                              className="text-xs px-2 py-0.5 rounded"
                              style={{ backgroundColor: DESIGN.colors.primary + '20', color: DESIGN.colors.primary }}
                            >
                              {artifact.type}
                            </span>
                            <span className="text-xs" style={{ color: DESIGN.colors.labTextMuted }}>
                              v{artifact.version}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p 
                        className="text-sm line-clamp-4"
                        style={{ color: DESIGN.colors.labTextMuted }}
                      >
                        {artifact.content?.text?.substring(0, 200)}...
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        /* Welcome Screen */
        <div 
          className="flex-1 rounded-2xl flex items-center justify-center"
          style={{ 
            backgroundColor: DESIGN.colors.labCard,
            border: `1px solid ${DESIGN.colors.labBorder}`
          }}
        >
          <div className="text-center max-w-md">
            <div className="relative inline-block mb-6">
              <FlaskConical className="w-20 h-20" style={{ color: DESIGN.colors.primary }} />
              <div 
                className="absolute inset-0"
                style={{ 
                  background: `radial-gradient(circle, ${DESIGN.colors.primary}30 0%, transparent 70%)`,
                  filter: 'blur(20px)'
                }}
              />
            </div>
            <h2 className="text-2xl font-bold mb-3" style={{ color: DESIGN.colors.labText }}>
              Welcome to THE LAB
            </h2>
            <p className="text-sm mb-8" style={{ color: DESIGN.colors.labTextMuted }}>
              Your command center for structured thinking and execution. 
              Create threads, use operators, and convert ideas into Projects, Tasks, and Focus blocks.
            </p>
            <div className="flex gap-4 justify-center">
              <button 
                onClick={handleNewThread}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
                style={{ 
                  background: `linear-gradient(135deg, ${DESIGN.colors.primary} 0%, ${DESIGN.colors.primaryDark} 100%)`,
                  color: 'white',
                  boxShadow: `0 0 20px ${DESIGN.colors.primary}50`
                }}
              >
                <Plus className="w-5 h-5" /> New Thread
              </button>
              <button 
                onClick={() => setShowOperators(true)}
                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold"
                style={{ 
                  backgroundColor: DESIGN.colors.labBackground,
                  color: DESIGN.colors.labText,
                  border: `1px solid ${DESIGN.colors.labBorder}`
                }}
              >
                <Workflow className="w-5 h-5" /> Browse Operators
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Operators Dialog */}
      <Dialog open={showOperators} onOpenChange={setShowOperators}>
        <DialogContent 
          className="max-w-4xl"
          style={{ backgroundColor: DESIGN.colors.labCard, borderColor: DESIGN.colors.labBorder }}
        >
          <DialogHeader>
            <DialogTitle style={{ color: DESIGN.colors.labText }}>LAB Operators</DialogTitle>
            <DialogDescription style={{ color: DESIGN.colors.labTextMuted }}>
              Pre-built workflows for structured outputs
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-center py-8" style={{ color: DESIGN.colors.labTextMuted }}>
              Operators panel coming soon with enhanced workflow templates
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert Wizard */}
      <ConvertWizard 
        isOpen={showConvertWizard}
        onClose={() => setShowConvertWizard(false)}
        onComplete={handleConversionComplete}
        threadTitle={selectedThread?.title}
        isDemo={selectedThread?.id === DEMO_THREAD.id}
      />
    </div>
  );
};

export default Lab;
